import env from './env';
import {clearAuthSession} from './auth/authStorage';
import {getKeycloakSsoConfig} from './api/keycloak-sso-config';

const AMS_STATE_PREFIX = 'ams_';

function base64UrlEncode(buffer) {
    return btoa(String.fromCharCode.apply(null, new Uint8Array(buffer)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}

function generateCodeVerifier() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return base64UrlEncode(array);
}

async function generateCodeChallenge(verifier) {
    const data = new TextEncoder().encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return base64UrlEncode(digest);
}

async function getSsoConfig() {
    return getKeycloakSsoConfig();
}

// Logout remains compatible with deployments that have not yet served the
// dynamic SSO configuration on this page.
function getRedirectUri() {
    return env.keycloakRedirectUri;
}

function getTokenEndpoint(config) {
    return `${config.issuer}/protocol/openid-connect/token`;
}

function getAuthEndpoint(config) {
    return `${config.issuer}/protocol/openid-connect/auth`;
}

function getLogoutEndpoint() {
    return `${env.keycloakUrl}/realms/${env.keycloakRealm}/protocol/openid-connect/logout`;
}

/**
 * Get the authorization code from URL search params
 */
export function getCodeFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return params.get('code');
}

/**
 * Check if the callback state was created by our keycloakLogin().
 * Returns the code_verifier if yes, null if it's an external SSO redirect.
 */
export function getCodeVerifierFromState() {
    const payload = getSsoStatePayload();
    return payload ? payload.v || null : null;
}

export function getUiOriginFromState() {
    const payload = getSsoStatePayload();
    return payload ? payload.o || null : null;
}

function getSsoStatePayload() {
    const params = new URLSearchParams(window.location.search);
    const state = params.get('state');
    console.log('[SSO] getCodeVerifierFromState — state:', state ? state.substring(0, 30) + '...' : 'null');
    if (!state || !state.startsWith(AMS_STATE_PREFIX)) {
        console.log('[SSO] State does not start with "' + AMS_STATE_PREFIX + '" — treating as external SSO');
        return null;
    }
    try {
        const payload = JSON.parse(atob(state.substring(AMS_STATE_PREFIX.length)));
        console.log('[SSO] Decoded code_verifier from state:', payload.v ? 'found' : 'missing');
        return payload;
    } catch (e) {
        console.error('[SSO] Failed to decode state payload:', e);
        return null;
    }
}

/**
 * Clear Keycloak callback params from URL without triggering a reload
 */
export function clearCallbackUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    url.searchParams.delete('session_state');
    url.searchParams.delete('iss');
    window.history.replaceState({}, document.title, url.pathname + url.hash);
}

/**
 * Get error from URL (e.g. when check-sso fails with login_required)
 */
export function getErrorFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return params.get('error');
}

/**
 * Clear error params from URL
 */
export function clearErrorUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('error');
    url.searchParams.delete('error_description');
    url.searchParams.delete('state');
    url.searchParams.delete('iss');
    window.history.replaceState({}, document.title, url.pathname + url.hash);
}

/**
 * Silent SSO check — redirect to Keycloak with prompt=none.
 * If user has active Keycloak session, redirects back with code.
 * If not, redirects back with error=login_required.
 */
export async function keycloakCheckSso() {
    const config = await getSsoConfig();
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    const statePayload = btoa(JSON.stringify({v: codeVerifier, o: config.uiOrigin}));
    const state = AMS_STATE_PREFIX + statePayload;

    const params = new URLSearchParams({
        client_id: config.clientId,
        redirect_uri: config.redirectUri,
        response_type: 'code',
        scope: 'openid',
        state: state,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        prompt: 'none',
    });

    const authUrl = `${getAuthEndpoint(config)}?${params.toString()}`;
    console.log('[SSO] keycloakCheckSso() — silent check, redirecting to Keycloak with prompt=none');
    console.log('[SSO] redirect_uri:', config.redirectUri);
    window.location.href = authUrl;
}

/**
 * Redirect to Keycloak login page.
 * Encodes the PKCE code_verifier inside the OAuth state parameter
 * so it survives cross-origin redirects.
 */
export async function keycloakLogin() {
    clearAuthSession();

    const config = await getSsoConfig();

    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    // Encode code_verifier in state so it survives cross-origin redirects
    const statePayload = btoa(JSON.stringify({v: codeVerifier, o: config.uiOrigin}));
    const state = AMS_STATE_PREFIX + statePayload;

    const params = new URLSearchParams({
        client_id: config.clientId,
        redirect_uri: config.redirectUri,
        response_type: 'code',
        scope: 'openid',
        state: state,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        prompt: 'login',
    });

    const authUrl = `${getAuthEndpoint(config)}?${params.toString()}`;
    console.log('[SSO] keycloakLogin() redirecting to:', authUrl.substring(0, 120) + '...');
    console.log('[SSO] redirect_uri:', config.redirectUri);
    window.location.href = authUrl;
}

/**
 * Exchange authorization code for Keycloak access token.
 * @param {string} code - the authorization code
 * @param {string|null} codeVerifier - PKCE code_verifier (from state param)
 */
export async function exchangeCodeForToken(code, codeVerifier) {
    const config = await getSsoConfig();
    console.log('[SSO] exchangeCodeForToken — code:', code.substring(0, 12) + '...',
        '| codeVerifier:', codeVerifier ? 'yes' : 'no',
        '| redirect_uri:', config.redirectUri);

    const body = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: config.clientId,
        code: code,
        redirect_uri: config.redirectUri,
    });

    if (codeVerifier) {
        body.append('code_verifier', codeVerifier);
    }

    console.log('[SSO] POST', getTokenEndpoint(config));
    const response = await fetch(getTokenEndpoint(config), {
        method: 'POST',
        headers: {'Content-Type': 'application/x-www-form-urlencoded'},
        body: body.toString(),
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('[SSO] Keycloak token exchange failed:', response.status, errorText);
        throw new Error(`Token exchange failed: ${response.status}`);
    }
    const data = await response.json();

    console.log('[SSO] Keycloak token exchange succeeded');

    localStorage.setItem('keycloakToken', data.access_token);
    if (data.refresh_token) {
        localStorage.setItem('keycloakRefreshToken', data.refresh_token);
    }
    if (data.id_token) {
        localStorage.setItem('keycloakIdToken', data.id_token);
    }

    return data.access_token;
}

/**
 * Refresh the Keycloak access token using the stored refresh token
 */
export async function refreshKeycloakToken() {
    const refreshToken = localStorage.getItem('keycloakRefreshToken');
    if (!refreshToken) {
        throw new Error('No refresh token');
    }

    const body = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: env.keycloakClientId,
        refresh_token: refreshToken,
    });

    const response = await fetch(getTokenEndpoint(), {
        method: 'POST',
        headers: {'Content-Type': 'application/x-www-form-urlencoded'},
        body: body.toString(),
    });

    if (!response.ok) {
        localStorage.removeItem('keycloakToken');
        localStorage.removeItem('keycloakRefreshToken');
        localStorage.removeItem('keycloakIdToken');
        throw new Error(`Token refresh failed: ${response.status}`);
    }

    const data = await response.json();
    localStorage.setItem('keycloakToken', data.access_token);
    if (data.refresh_token) {
        localStorage.setItem('keycloakRefreshToken', data.refresh_token);
    }
    if (data.id_token) {
        localStorage.setItem('keycloakIdToken', data.id_token);
    }

    return data.access_token;
}

/**
 * Redirect to Keycloak logout endpoint
 */
export function keycloakLogout() {
    const idToken = localStorage.getItem('keycloakIdToken');

    clearAuthSession();

    const params = new URLSearchParams({
        client_id: env.keycloakClientId,
        post_logout_redirect_uri: getRedirectUri(),
    });

    if (idToken) {
        params.append('id_token_hint', idToken);
    }

    window.location.href = `${getLogoutEndpoint()}?${params.toString()}`;
}

export function isKeycloakAuthenticated() {
    return !!localStorage.getItem('keycloakToken');
}

export function getKeycloakToken() {
    return localStorage.getItem('keycloakToken');
}

// Backward-compatible default export (mimics keycloak-js interface)
const keycloak = {
    get authenticated() {
        return isKeycloakAuthenticated();
    },
    get token() {
        return getKeycloakToken();
    },
    login: () => keycloakLogin(),
    logout: () => {
        keycloakLogout();
        return new Promise(() => {
        });
    },
    updateToken: () => refreshKeycloakToken(),
};

export const initKeycloak = () => Promise.resolve(false);

export default keycloak;
